/*
 * SystemComponentsTable - 组件详细列表
 * 展示 System 下所有 Component 的详细信息，包括 ArgoCD 状态、环境配置等
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Link,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { RELATION_HAS_PART } from '@backstage/catalog-model';
import { Progress, EmptyState } from '@backstage/core-components';
import { argoCDApiRef } from '@roadiehq/backstage-plugin-argo-cd';
import { parseEnvironmentConfigs, EnvironmentConfig, getArgoCDProjectInfo } from './types';
import { ComponentDetailCard, ComponentDetailData } from './ComponentDetailCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
  }),
);

// 组件详细信息（使用共享的 ComponentDetailData 类型）
type ComponentDetail = ComponentDetailData;

/**
 * 根据 destination 信息匹配到 System 的环境配置
 */
const matchEnvironment = (
  clusterName: string | undefined,
  server: string | undefined,
  namespace: string | undefined,
  environments: EnvironmentConfig[],
): { name: string; label: string } | undefined => {
  if (!clusterName && !server && !namespace) {
    return undefined;
  }

  for (const env of environments) {
    const clusterMatch = clusterName && (
      env.clusterName === clusterName ||
      env.clusterName === clusterName.replace(/^https?:\/\//, '').split(':')[0]
    );
    const serverMatch = server && (
      env.server === server ||
      env.server.replace(/^https?:\/\//, '').split(':')[0] === server.replace(/^https?:\/\//, '').split(':')[0]
    );
    const namespaceMatch = namespace && env.namespace === namespace;

    if (clusterMatch || (serverMatch && namespaceMatch)) {
      return { name: env.name, label: env.label };
    }
  }

  return undefined;
};

export const SystemComponentsTable: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const argoCDApi = useApi(argoCDApiRef);

  // 使用 useMemo 稳定 environments，避免无限循环
  const entityId = entity.metadata.uid || entity.metadata.etag || `${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const environments = useMemo(() => parseEnvironmentConfigs(entity), [entityId]);

  // 获取 System 的 ArgoCD Project 信息（用于获取默认 instance-name）
  const argocdProject = useMemo(() => getArgoCDProjectInfo(entity), [entityId]);

  // 获取 System 下的所有 Component
  const { entities: components, loading: componentsLoading, error: componentsError } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });

  // 组件详细信息
  const [componentDetails, setComponentDetails] = useState<ComponentDetail[]>([]);
  const [argoLoading, setArgoLoading] = useState(false);
  const [argoError, setArgoError] = useState<Error | null>(null);

  // 使用 components 的稳定标识符作为依赖，避免无限循环
  const componentsKey = useMemo(
    () => components?.map(c => `${c.metadata.namespace || 'default'}/${c.metadata.name}`).sort().join(',') || '',
    [components],
  );

  // 获取每个组件的 ArgoCD 应用状态
  useEffect(() => {
    if (!components || components.length === 0) {
      setComponentDetails([]);
      setArgoLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchComponentDetails = async () => {
      setArgoLoading(true);
      setArgoError(null);

      const details: ComponentDetail[] = [];

      for (const component of components) {
        const annotations = component.metadata?.annotations || {};
        const appName = annotations['argocd/app-name'];
        const appSelector = annotations['argocd/app-selector'];
        // 获取 ArgoCD 实例名称：优先使用 component 注解，其次使用 System 的 ArgoCD Project，最后使用默认值
        const instanceName = annotations['argocd/instance-name'] || argocdProject?.instanceName || 'development';

        // 解析 owner
        const owner = (component.spec?.owner as string) || 'unknown';
        const ownerParts = owner.split('/');
        const ownerName = ownerParts[ownerParts.length - 1];

        if (!appName && !appSelector) {
          // 没有 ArgoCD 配置
          details.push({
            componentName: component.metadata.name,
            componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
            componentType: component.spec?.type as string | undefined,
            componentDescription: component.metadata.description,
            owner: ownerName,
            ownerRef: owner,
            lifecycle: component.spec?.lifecycle as string | undefined,
            hasArgoCD: false,
            healthStatus: 'Unknown',
            syncStatus: 'Unknown',
          });
          continue;
        }

        try {
          let apps: any[] = [];

          if (appName) {
            const appDetails = await argoCDApi.getAppDetails({
              url: `/argoInstance/${instanceName}`,
              appName,
              instance: instanceName,
            });
            if (appDetails) {
              apps = [appDetails];
            }
          } else if (appSelector) {
            const response = await argoCDApi.getAppListDetails({
              url: `/argoInstance/${instanceName}`,
              appSelector,
              instance: instanceName,
            });
            apps = response?.items || [];
          }

          if (apps.length > 0) {
            const app = apps[0];
            const appAny = app as any;
            
            const dest = appAny.spec?.destination || {};
            // 优先使用 component 注解中的 argocd/dest-server 作为 clusterName
            const clusterNameFromAnnotation = annotations['argocd/dest-server'];
            const clusterName = clusterNameFromAnnotation || dest.name;
            const server = dest.server;
            const namespace = dest.namespace || 'default';
            
            const matchedEnv = matchEnvironment(clusterName, server, namespace, environments);
            
            details.push({
              componentName: component.metadata.name,
              componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
              componentType: component.spec?.type as string | undefined,
              componentDescription: component.metadata.description,
              owner: ownerName,
              ownerRef: owner,
              lifecycle: component.spec?.lifecycle as string | undefined,
              appName: app.metadata?.name,
              instanceName,
              hasArgoCD: true,
              healthStatus: (app.status?.health?.status as HealthStatus) || 'Unknown',
              syncStatus: app.status?.sync?.status || 'Unknown',
              clusterName,
              server,
              namespace,
              environment: matchedEnv?.name,
              environmentLabel: matchedEnv?.label,
              syncRevision: app.status?.sync?.revision,
              lastSyncTime: app.status?.operationState?.finishedAt || app.status?.sync?.revision,
            });
          } else {
            details.push({
              componentName: component.metadata.name,
              componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
              componentType: component.spec?.type as string | undefined,
              componentDescription: component.metadata.description,
              owner: ownerName,
              ownerRef: owner,
              lifecycle: component.spec?.lifecycle as string | undefined,
              appName,
              instanceName,
              hasArgoCD: true,
              healthStatus: 'Missing',
              syncStatus: 'Unknown',
            });
          }
        } catch (err) {
          details.push({
            componentName: component.metadata.name,
            componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
            componentType: component.spec?.type as string | undefined,
            componentDescription: component.metadata.description,
            owner: ownerName,
            ownerRef: owner,
            lifecycle: component.spec?.lifecycle as string | undefined,
            appName,
            instanceName,
            hasArgoCD: true,
            healthStatus: 'Unknown',
            syncStatus: 'Unknown',
          });
        }
      }

      if (!cancelled) {
        setComponentDetails(details);
        setArgoLoading(false);
      }
    };

    fetchComponentDetails();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentsKey, entityId, argoCDApi]);

  const loading = componentsLoading || argoLoading;
  const error = componentsError || argoError;

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <EmptyState
        missing="info"
        title="加载组件失败"
        description={error.message}
      />
    );
  }

  if (!components || components.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="暂无组件"
        description="此 System 下还没有注册任何 Component。使用「服务组件注册」模板来添加组件。"
        action={
          <Link href="/create" color="primary">
            前往创建
          </Link>
        }
      />
    );
  }

  return (
    <Card className={classes.card}>
      <CardHeader title={`组件列表 (${componentDetails.length})`} />
      <CardContent>
        {componentDetails.map((detail) => (
          <ComponentDetailCard key={detail.componentRef} detail={detail} showOwner={true} />
        ))}
      </CardContent>
    </Card>
  );
};
